/* eslint-disable max-lines */

import {
  ADLdapConfigData,
  ADLdapIwaConfigData,
  AddDirectory,
  DirectoryConfigData,
  DomainSelectionData,
  GroupSelectionData,
  MapAttributeData,
  PreviewData,
  UserSelectionData,
  WizardData,
  WizardState
} from '../../types/state/AddDirectory';
import {
  ADTestConnectionData,
  AsyncOpState,
  DirectoryType,
  DryRunResult,
  GroupProfile,
  IdentityUserInfo,
  PersistedAddADData,
  UpdateGroupAndUserMappingRequest,
  UserAttributeMapping,
  VidmADOverLdapIwaRequestDTO,
  VidmADOverLdapRequestDTO,
  VidmDirConfigIwa,
  VidmDirConfigLdap,
  VidmDirectoryCompleteData
} from 'vrlcm-gui-types';
import { mapAlertItem } from 'vrlcm-gui-utils';
import { l10n } from 'i18n/i18nUtil';

const ADAttributeConstants = [
  'mail',
  'sAMAccountName',
  'givenName',
  'telephoneNumber',
  'sn',
  'distinguishedName',
  'userAccountControl',
  'userPrincipalName',
  'canonicalName',
  'employeeID'
];

export const userMappingToMapAttrData = (
  userAttributeMappings: UserAttributeMapping[]
): MapAttributeData => {
  const allAttributesInAD = [...ADAttributeConstants];
  return {
    userAttributeMappings: [
      ...userAttributeMappings.filter(u => u.required),
      ...userAttributeMappings.filter(u => !u.required)
    ],
    allAttributesInAD
  };
};

export const mapAttrDataToUserMapping = (
  mapAttributeData: MapAttributeData
): UserAttributeMapping[] => {
  return mapAttributeData.userAttributeMappings;
};

export const directoryDataToTCData = (
  data: DirectoryConfigData,
  directoryType: DirectoryType
): ADTestConnectionData => {
  return {
    directoryType: directoryType.toString(),
    directoryConfig:
      directoryType === DirectoryType.ACTIVE_DIRECTORY_LDAP
        ? directoryDataToAdLdapConfig(data as ADLdapConfigData, directoryType)
        : directoryDataToAdLdapIwaConfig(
            data as ADLdapIwaConfigData,
            directoryType
          )
  };
};

const directoryDataToAdLdapConfig = (
  data: ADLdapConfigData,
  directoryType: DirectoryType
): VidmADOverLdapRequestDTO => {
  const useStartTls = data.certificateData.secureSSLConnectionEnabled;
  return {
    connectorInstanceId: data.syncDetails.connectorInstanceId,
    directoryType,
    name: data.directoryName,
    bindDN: data.bindUserData.bindDN,
    bindPassword: data.bindUserData.bindPassword,
    baseDN: data.bindUserData.baseDN,
    directorySearchAttribute: data.syncDetails.directorySearchAttribute,
    useSRV: data.serverLocation.aDSupportsDNSServiceLocation,
    domainControllerPort: data.serverLocation.dnsServiceData.serverPort,
    domainControllerHost: data.serverLocation.dnsServiceData.serverHost,
    useGlobalCatalog:
      data.serverLocation.dnsServiceData.directoryHasGlobalCatalog,
    useStartTls:
      data.serverLocation.aDSupportsDNSServiceLocation && useStartTls,
    useSsl: !data.serverLocation.aDSupportsDNSServiceLocation && useStartTls,
    sslCertificate:
      useStartTls && data.certificateData.sslCertificate
        ? data.certificateData.sslCertificate
        : '',
    syncConfigurationEnabled: false
  };
};

const directoryDataToAdLdapIwaConfig = (
  data: ADLdapIwaConfigData,
  directoryType: DirectoryType
): VidmADOverLdapIwaRequestDTO => {
  return {
    connectorInstanceId: data.syncDetails.connectorInstanceId,
    directoryType,
    name: data.directoryName,
    directorySearchAttribute: data.syncDetails.directorySearchAttribute,
    useStartTls: data.certificateData.secureSSLConnectionEnabled,
    useSsl: false,
    sslCertificate: data.certificateData.sslCertificate || '',
    bindUsername: data.bindUserIwaData.bindUserName,
    bindPassword: data.bindUserIwaData.bindPassword,
    domainName: data.joinDomainData
      ? data.joinDomainData.domainName
      : undefined,
    domainAdminUsername: data.joinDomainData
      ? data.joinDomainData.domainAdminUserName
      : undefined,
    domainAdminPassword: data.joinDomainData
      ? data.joinDomainData.domainAdminPassword
      : undefined,
    syncConfigurationEnabled: false,
    connectorDomainJoined: !data.joinDomainData
  };
};

export const userGroupSelectionToApiData = (data: {
  directoryConfigId: string;
  userSelectionData?: UserSelectionData;
  groupSelectionData?: GroupSelectionData;
  groupDNToNumTotalSubGroupsMap?: {
    [groupDN: string]: number;
  };
}): UpdateGroupAndUserMappingRequest => {
  return {
    directoryConfigId: data.directoryConfigId,
    syncProfileUser: data.userSelectionData
      ? {
          userFilters: [],
          identityUserInfo: data.userSelectionData.userDNs.reduce(
            (map: any, userDN) => {
              map[userDN] = {
                selected: true,
                numSelected: 0,
                numTotal: 0,
                users: []
              };
              return map;
            },
            {}
          )
        }
      : undefined,
    syncProfileGroup: data.groupSelectionData
      ? {
          excludeNestedGroupMembers: !data.groupSelectionData.syncNestedGroup,
          identityGroupInfo: data.groupSelectionData.selectedGroups.reduce(
            (map: any, group) => {
              map[group.groupDN] = {
                mappedGroupData: !group.allSubGroupsSelected
                  ? group.selectedSubGroups.map(g => ({ ...g, selected: true }))
                  : [],
                selected: group.allSubGroupsSelected,
                numSelected: group.selectedSubGroups.length,
                numTotal: data.groupDNToNumTotalSubGroupsMap
                  ? data.groupDNToNumTotalSubGroupsMap[group.groupDN]
                  : 0
              };
              return map;
            },
            {}
          )
        }
      : undefined,
    triggerDryrun: false
  };
};

export const userGroupUpdateResponseToPreviewData = (
  response: DryRunResult,
  mappedUserAttributeCount: number,
  selectedDomainCount: number
): PreviewData => {
  return {
    groups: {
      add: response.groupAdd ? response.groupAdd.length : 0,
      remove: response.groupRemove ? +response.groupRemove.length : 0,
      update: response.groupUpdate ? +response.groupUpdate.length : 0
    },
    users: {
      add: response.userAdd ? +response.userAdd.length : 0,
      remove: response.userRemove ? +response.userRemove.length : 0,
      update: response.userUpdate ? +response.userUpdate.length : 0
    },
    mappedUserAttributeCount,
    selectedDomainCount,
    alerts: response.alerts ? response.alerts.map(al => mapAlertItem(al)) : []
  };
};

const StepTitles = [
  l10n('authzn.dirmgmt.dirDetail'),
  l10n('authzn.dirmgmt.domainSelectionDetail'),
  l10n('authzn.dirmgmt.mapAttribute'),
  l10n('authzn.dirmgmt.groupSelection'),
  l10n('authzn.dirmgmt.userSelection'),
  l10n('authzn.dirmgmt.dryRunCheck')
];

export const getInitialWizardData = (
  directoryType: DirectoryType
): WizardData | undefined => {
  if (directoryType === DirectoryType.ACTIVE_DIRECTORY_LDAP) {
    return {
      directoryType,
      directoryData: {
        directoryName: '',
        syncDetails: {
          connectorInstanceId: '',
          authenticationEnabled: true,
          directorySearchAttribute: 'sAMAccountName'
        },
        serverLocation: {
          aDSupportsDNSServiceLocation: true,
          dnsServiceData: {
            directoryHasGlobalCatalog: false,
            serverHost: '',
            serverPort: 389
          }
        },
        certificateData: {
          secureSSLConnectionEnabled: false,
          sslCertificate: ''
        },
        bindUserData: {
          baseDN: '',
          bindDN: '',
          bindPassword: ''
        }
      },
      mapAttributeData: {
        userAttributeMappings: [],
        allAttributesInAD: []
      },
      groupSelectionData: {
        syncNestedGroup: false,
        selectedGroups: []
      },
      userSelectionData: {
        userDNs: []
      },
      domainSelectionData: {
        domains: []
      },
      previewData: {
        alerts: [],
        selectedDomainCount: 0,
        groups: {
          add: 0,
          remove: 0,
          update: 0
        },
        users: {
          add: 0,
          remove: 0,
          update: 0
        },
        mappedUserAttributeCount: 0
      }
    };
  } else if (directoryType === DirectoryType.ACTIVE_DIRECTORY_IWA) {
    return {
      directoryType,
      directoryData: {
        directoryName: '',
        syncDetails: {
          connectorInstanceId: '',
          authenticationEnabled: true,
          directorySearchAttribute: 'sAMAccountName'
        },
        joinDomainData: {
          domainAdminPassword: '',
          domainAdminUserName: '',
          domainName: ''
        },
        certificateData: {
          secureSSLConnectionEnabled: false,
          sslCertificate: ''
        },
        bindUserIwaData: {
          bindPassword: '',
          bindUserName: ''
        }
      },
      mapAttributeData: {
        userAttributeMappings: [],
        allAttributesInAD: []
      },
      groupSelectionData: {
        syncNestedGroup: false,
        selectedGroups: []
      },
      userSelectionData: {
        userDNs: []
      },
      domainSelectionData: {
        domains: []
      },
      previewData: {
        alerts: [],
        selectedDomainCount: 0,
        groups: {
          add: 0,
          remove: 0,
          update: 0
        },
        users: {
          add: 0,
          remove: 0,
          update: 0
        },
        mappedUserAttributeCount: 0
      }
    };
  }
};

const persistedDataToDirectoryConfigFormData = (
  directoryType: DirectoryType,
  persistedData: PersistedAddADData
): DirectoryConfigData => {
  const { directoryConfig, connectors } = persistedData.directoryCompleteData;
  const baseData = {
    directoryName: directoryConfig.name,
    syncDetails: {
      connectorInstanceId:
        connectors && connectors.length > 0 ? connectors[0].instanceId : '',
      authenticationEnabled: true,
      directorySearchAttribute: directoryConfig.directorySearchAttribute
    },
    serverLocation: {
      aDSupportsDNSServiceLocation: directoryConfig.useSRV,
      dnsServiceData: {
        directoryHasGlobalCatalog: directoryConfig.useGlobalCatalog,
        serverHost: directoryConfig.domainControllerHost || '',
        serverPort: directoryConfig.domainControllerPort
          ? +directoryConfig.domainControllerPort
          : 389
      }
    },
    certificateData: {
      secureSSLConnectionEnabled: directoryConfig.useStartTls,
      sslCertificate: directoryConfig.sslCertificate
    }
  };
  if (directoryType === DirectoryType.ACTIVE_DIRECTORY_LDAP) {
    const directoryConfigLdap = directoryConfig as VidmDirConfigLdap;
    const configData: ADLdapConfigData = {
      ...baseData,
      bindUserData: {
        baseDN: directoryConfigLdap.baseDN,
        bindDN: directoryConfigLdap.bindDN,
        bindPassword: ''
      }
    };
    return configData;
  } else {
    const directoryConfigIwa = directoryConfig as VidmDirConfigIwa;
    const configData: ADLdapIwaConfigData = {
      ...baseData,
      joinDomainData: {
        domainAdminPassword: '',
        domainAdminUserName: directoryConfigIwa.joinDomainAdminUsername,
        domainName: directoryConfigIwa.joinDomainName
      },
      bindUserIwaData: {
        bindPassword: '',
        bindUserName: directoryConfigIwa.username
      }
    };
    return configData;
  }
};

const persistedDataToMapAttributeData = (
  persistedData: PersistedAddADData
): MapAttributeData => {
  return userMappingToMapAttrData(
    persistedData.directoryCompleteData.directoryConfig.userAttributeMappings ||
      []
  );
};

const persistedDataToDomainSelectionData = (
  persistedData: PersistedAddADData
): DomainSelectionData => {
  return {
    domains: persistedData.directoryCompleteData.domains || []
  };
};

const groupProfileToGroupSelectionData = (
  groupProfile?: GroupProfile
): GroupSelectionData => {
  return {
    syncNestedGroup: groupProfile
      ? !groupProfile.excludeNestedGroupMembers
      : false,
    selectedGroups: Object.entries(
      groupProfile ? groupProfile.identityGroupInfo : {}
    ).map(([groupDN, details]) => {
      return {
        groupDN,
        selectedSubGroups: groupProfile
          ? groupProfile.identityGroupInfo.selected
            ? []
            : details.mappedGroupData.filter(s => s.selected)
          : [],
        allSubGroupsSelected: details.selected,
        syncResult: {
          syncState: AsyncOpState.NotStarted
        }
      };
    })
  };
};

const persistedDataToGroupSelectionData = (
  persistedData: PersistedAddADData
): GroupSelectionData => {
  return groupProfileToGroupSelectionData(
    persistedData.directoryCompleteData.groupProfile
  );
};

export const identityUserInfoToSelectedUserDNs = (
  identityUserInfo: IdentityUserInfo
): string[] => {
  return Object.entries(identityUserInfo)
    .filter(([userDN, details]) => details.selected)
    .map(([userDN]) => userDN);
};

const persistedDataToUserSelectionData = (
  persistedData: PersistedAddADData
): UserSelectionData => {
  return {
    userDNs: identityUserInfoToSelectedUserDNs(
      persistedData.directoryCompleteData.userProfile
        ? persistedData.directoryCompleteData.userProfile.identityUserInfo
        : {}
    )
  };
};

export const persistedDataToWizardData = (
  directoryConfigId: string,
  persistedData: PersistedAddADData
): WizardData => {
  const directoryType =
    persistedData.directoryCompleteData.directoryConfig.directoryType;
  return {
    directoryType,
    directoryConfigId,
    directoryData: persistedDataToDirectoryConfigFormData(
      directoryType,
      persistedData
    ),
    mapAttributeData: persistedDataToMapAttributeData(persistedData),
    domainSelectionData: persistedDataToDomainSelectionData(persistedData),
    groupSelectionData: persistedDataToGroupSelectionData(persistedData),
    userSelectionData: persistedDataToUserSelectionData(persistedData),
    previewData: {
      alerts: [],
      selectedDomainCount: 0,
      groups: {
        add: 0,
        remove: 0,
        update: 0
      },
      users: {
        add: 0,
        remove: 0,
        update: 0
      },
      mappedUserAttributeCount: 0
    }
  };
};

export const getInitialWizardState = (updatePage: boolean): WizardState => {
  const currentStep = updatePage ? 1 : 0;
  return {
    wizardLevelState: {
      submitted: false,
      inprogress: false,
      overlayMessage: ''
    },
    currentStep,
    stepCount: 6,
    steps: StepTitles.map((title, i) => ({
      title,
      valid: false,
      disabled: i > currentStep,
      state: i < currentStep ? 'complete' : undefined
    }))
  };
};

export const isDirectoryWithGlobalCatalog = (
  addDirectory: AddDirectory,
  directoryType?: DirectoryType
) =>
  directoryType === DirectoryType.ACTIVE_DIRECTORY_LDAP &&
  addDirectory.wizardData &&
  (addDirectory.wizardData.directoryData as ADLdapConfigData).serverLocation
    .dnsServiceData.directoryHasGlobalCatalog;

export const vidmDirConfigLdapToVidmADOverLdapIwaRequestDTO = (
  directory: VidmDirectoryCompleteData,
  bindPassword: string
): VidmADOverLdapRequestDTO => {
  const directoryConfig = directory.directoryConfig as VidmDirConfigLdap;
  return {
    ...directoryConfig,
    bindPassword,
    connectorInstanceId:
      directory.connectors && directory.connectors.length > 0
        ? directory.connectors[0].instanceId
        : ''
  };
};

export const vidmDirConfigIwaToVidmADOverLdapIwaRequestDTO = (
  directory: VidmDirectoryCompleteData,
  bindPassword: string,
  domainAdminPassword: string
): VidmADOverLdapIwaRequestDTO => {
  const directoryConfig = directory.directoryConfig as VidmDirConfigIwa;
  return {
    ...directoryConfig,
    connectorDomainJoined: !(
      directoryConfig.joinDomainName && directoryConfig.joinDomainAdminUsername
    ),
    connectorInstanceId:
      directory.connectors && directory.connectors.length > 0
        ? directory.connectors[0].instanceId
        : '',
    bindPassword,
    bindUsername: directoryConfig.username,
    domainName: directoryConfig.joinDomainName,
    domainAdminPassword,
    domainAdminUsername: directoryConfig.joinDomainAdminUsername
  };
};
